/**
 * shipmentEventPresentation - Catálogo visual dos eventos da remessa (ícone, tom, rótulo),
 * no mesmo formato de `serviceOrderEventPresentation` para alimentar a timeline compartilhada.
 *
 * RELEASE: 2026-10-16 v1.1.0 - Descrição detalhada via `metadata` (item, vínculo e campos alterados);
 *          eventos antigos sem metadata mantêm o texto genérico.
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial.
 */
import { ArrowRightLeft, Link2, Minus, Pencil, Plus, RotateCcw, Truck, Unlink, type LucideIcon } from 'lucide-react';
import type { ServiceOrderTimelineItem } from '@/hooks/useServiceOrderDetails';
import { SHIPMENT_STATUS_META, type ShipmentStatus } from './shipmentStatus';

interface Presentation { Icon: LucideIcon; tone: string; badgeTone: string; badgeLabel: string }

const MAP: Record<string, Presentation> = {
  created: { Icon: Truck, tone: 'bg-primary/10 text-primary', badgeTone: 'bg-primary/10 text-primary', badgeLabel: 'Remessa criada' },
  updated: { Icon: Pencil, tone: 'bg-muted text-muted-foreground', badgeTone: 'bg-muted text-muted-foreground', badgeLabel: 'Dados atualizados' },
  status_changed: { Icon: ArrowRightLeft, tone: 'bg-accent text-accent-foreground', badgeTone: 'bg-accent text-accent-foreground', badgeLabel: 'Situação alterada' },
  item_added: { Icon: Plus, tone: 'bg-secondary text-secondary-foreground', badgeTone: 'bg-secondary text-secondary-foreground', badgeLabel: 'Item incluído' },
  item_removed: { Icon: Minus, tone: 'bg-destructive/10 text-destructive', badgeTone: 'bg-destructive/10 text-destructive', badgeLabel: 'Item removido' },
  linked: { Icon: Link2, tone: 'bg-primary/10 text-primary', badgeTone: 'bg-primary/10 text-primary', badgeLabel: 'Registro vinculado' },
  unlinked: { Icon: Unlink, tone: 'bg-muted text-muted-foreground', badgeTone: 'bg-muted text-muted-foreground', badgeLabel: 'Registro desvinculado' },
  return_created: { Icon: RotateCcw, tone: 'bg-accent text-accent-foreground', badgeTone: 'bg-accent text-accent-foreground', badgeLabel: 'Retorno gerado' },
};

const statusLabel = (s?: string | null) => (s ? SHIPMENT_STATUS_META[s as ShipmentStatus]?.label ?? s : '—');

export function getShipmentEventPresentation(type?: string): Presentation {
  return MAP[type ?? ''] ?? { Icon: Pencil, tone: 'bg-muted text-muted-foreground', badgeTone: 'bg-muted text-muted-foreground', badgeLabel: 'Evento' };
}

const ITEM_LABEL: Record<string, string> = { tracker: 'Rastreador', chip: 'Chip', kit: 'Kit', free: 'Item livre' };
const ENTITY_LABEL: Record<string, string> = { service_order: 'O.S.', deal: 'Negócio', task: 'Tarefa', customer: 'Cliente', vehicle: 'Veículo' };
const FIELD_LABEL: Record<string, string> = {
  recipient: 'Destinatário',
  carrier: 'Transportadora', carrier_service: 'Serviço', tracking_code: 'Rastreio',
  freight_cost: 'Frete', expected_delivery_date: 'Previsão', notes: 'Observação',
};

/** Formata um valor alterado para leitura humana. */
function fmtValue(field: string, v: unknown): string {
  if (v == null || v === '') return '—';
  if (field === 'freight_cost') return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  if (field === 'expected_delivery_date') { const [y, m, d] = String(v).split('-'); return `${d}/${m}/${y}`; }
  return String(v);
}

/** Texto detalhado a partir de `metadata` (null quando o evento é antigo e não tem detalhes). */
export function detailShipmentEvent(e: ServiceOrderTimelineItem): string | null {
  const m = (e.metadata ?? {}) as Record<string, any>;
  const type = e.event_type as string | undefined;
  if ((type === 'item_added' || type === 'item_removed') && m.item_type) {
    const what = ITEM_LABEL[m.item_type] ?? 'Item';
    const label = m.label ? (m.item_type === 'tracker' ? `IMEI ${m.label}` : m.item_type === 'chip' ? `ICCID ${m.label}` : m.label) : null;
    return `${what}${label ? ` ${label}` : ''} ${type === 'item_added' ? 'incluído' : 'removido'}`;
  }
  if ((type === 'linked' || type === 'unlinked') && m.entity_type) {
    return `${ENTITY_LABEL[m.entity_type] ?? 'Registro'}${m.label ? ` ${m.label}` : ''} ${type === 'linked' ? 'vinculado(a)' : 'desvinculado(a)'}`;
  }
  if (type === 'updated' && m.changes && Object.keys(m.changes).length) {
    return Object.entries(m.changes as Record<string, { old: unknown; new: unknown }>)
      .map(([k, c]) => `${FIELD_LABEL[k] ?? k}: ${fmtValue(k, c?.old)} → ${fmtValue(k, c?.new)}`).join(' · ');
  }
  if (type === 'created' && m.return_of) return `Retorno da remessa ${m.return_of}`;
  return null;
}

export function describeShipmentEvent(e: ServiceOrderTimelineItem, omitNote = false): string {
  const type = e.event_type as string | undefined;
  const base = type === 'status_changed' ? `${statusLabel(e.old_value)} → ${statusLabel(e.new_value)}`
    : detailShipmentEvent(e) ?? getShipmentEventPresentation(type).badgeLabel;
  return !omitNote && e.note ? `${base} · ${e.note}` : base;
}

export function getShipmentEventNote(e: ServiceOrderTimelineItem): { label: string; text: string } | null {
  return e.note ? { label: 'Detalhe', text: e.note } : null;
}
